import React from "react";
import { Box, Container, Grid, Typography, Button, Chip } from "@mui/material";
import {
  Facebook,
  Instagram,
  LinkedIn,
  WhatsApp,
  YouTube,
  Security,
  VerifiedUser,
  CloudSync,
  Support,
} from "@mui/icons-material";
import {
  SOCIAL_MEDIA_LINKS,
  COMPANY_INFO,
  FOOTER_LINKS
} from "../../Config/index";
import CLogo from "../../Components/Reusable/Logo";

const Footer = ({ onGetStarted }) => {
  return (
    <Box
      sx={{
        background:
          "linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)",
        color: "white",
        pt: 8,
        pb: 4,
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Animated Background Elements */}
      <Box
        sx={{
          position: "absolute",
          width: "100%",
          height: "100%",
          overflow: "hidden",
          opacity: 0.1,
        }}
      >
        {[...Array(15)].map((_, i) => (
          <Box
            key={i}
            sx={{
              position: "absolute",
              width: `${30 + Math.random() * 60}px`,
              height: `${30 + Math.random() * 60}px`,
              background: "rgba(255,255,255,0.1)",
              borderRadius: "50%",
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              animation: `floatFooter ${
                10 + Math.random() * 20
              }s infinite ease-in-out`,
              animationDelay: `${Math.random() * 5}s`,
              "@keyframes floatFooter": {
                "0%, 100%": { transform: "translate(0, 0)" },
                "50%": {
                  transform: `translate(${Math.random() * 100 - 50}px, ${
                    Math.random() * 100 - 50
                  }px)`,
                },
              },
            }}
          />
        ))}
      </Box>

      <Container sx={{ position: "relative", zIndex: 1 }}>
        <Grid container spacing={6}>
          {/* Brand Column */}
          <Grid item xs={12} md={4}>
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <CLogo size="44px" textSize="22px" isDarkTheme={true} />
              </Box>
              <Typography
                variant="body2"
                sx={{ opacity: 0.8, mb: 3, lineHeight: 1.6 }}
              >
                {COMPANY_INFO.description}
              </Typography>
              <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                <Chip
                  icon={<Security sx={{ fontSize: 16 }} />}
                  label="SOC 2 Certified"
                  size="small"
                  sx={{
                    bgcolor: "rgba(67,233,123,0.2)",
                    color: "#4ade80",
                    border: "1px solid rgba(67,233,123,0.3)",
                  }}
                />
                <Chip
                  icon={<VerifiedUser sx={{ fontSize: 16 }} />}
                  label="Trusted by 50K+"
                  size="small"
                  sx={{
                    bgcolor: "rgba(79,172,254,0.2)",
                    color: "#4facfe",
                    border: "1px solid rgba(79,172,254,0.3)",
                  }}
                />
              </Box>
            </Box>
          </Grid>

          {/* Quick Links */}
          <Grid item xs={12} sm={6} md={2}>
            <Typography variant="h6" sx={{ fontWeight: "bold", mb: 3 }}>
              Product
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {FOOTER_LINKS.product.map((link) => (
                <Button
                  key={link.label}
                  component="a"
                  href={link.url}
                  variant="text"
                  sx={{
                    color: "rgba(255,255,255,0.7)",
                    justifyContent: "flex-start",
                    textTransform: "none",
                    fontSize: "0.95rem",
                    p: 0,
                    "&:hover": {
                      color: "white",
                      background: "transparent",
                      transform: "translateX(5px)",
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  {link.label}
                </Button>
              ))}
            </Box>
          </Grid>

          {/* Company */}
          <Grid item xs={12} sm={6} md={2}>
            <Typography variant="h6" sx={{ fontWeight: "bold", mb: 3 }}>
              Company
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {FOOTER_LINKS.company.map((link) => (
                <Button
                  key={link.label}
                  component="a"
                  href={link.url}
                  target={link.target || "_self"}
                  rel={link.target === "_blank" ? "noopener noreferrer" : undefined}
                  variant="text"
                  sx={{
                    color: "rgba(255,255,255,0.7)",
                    justifyContent: "flex-start",
                    textTransform: "none",
                    fontSize: "0.95rem",
                    p: 0,
                    "&:hover": {
                      color: "white",
                      background: "transparent",
                      transform: "translateX(5px)",
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  {link.label}
                </Button>
              ))}
            </Box>
          </Grid>

          {/* Resources */}
          <Grid item xs={12} sm={6} md={2}>
            <Typography variant="h6" sx={{ fontWeight: "bold", mb: 3 }}>
              Resources
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {FOOTER_LINKS.resources.map((link) => (
                <Button
                  key={link.label}
                  component="a"
                  href={link.url}
                  variant="text"
                  sx={{
                    color: "rgba(255,255,255,0.7)",
                    justifyContent: "flex-start",
                    textTransform: "none",
                    fontSize: "0.95rem",
                    p: 0,
                    "&:hover": {
                      color: "white",
                      background: "transparent",
                      transform: "translateX(5px)",
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  {link.label}
                </Button>
              ))}
            </Box>
          </Grid>

          {/* Connect with Us */}
          <Grid item xs={12} sm={6} md={2}>
            <Typography variant="h6" sx={{ fontWeight: "bold", mb: 3 }}>
              Connect
            </Typography>

            {/* Contact Us Button */}
            <Button
              component="a"
              href="https://www.oruclicks.com/new-enquiry"
              target="_blank"
              rel="noopener noreferrer"
              variant="contained"
              sx={{
                width: '100%',
                mb: 2,
                background: "linear-gradient(135deg, #667eea, #764ba2)",
                color: "white",
                fontWeight: "bold",
                textTransform: "none",
                borderRadius: 2,
                px: 2,
                py: 1.5,
                "&:hover": {
                  background: "linear-gradient(135deg, #5a67d8, #6b46c1)",
                  transform: "translateY(-2px)",
                  boxShadow: "0 6px 20px rgba(102,126,234,0.4)",
                },
                transition: "all 0.3s ease",
              }}
            >
              📧 Contact Us
            </Button>

            {/* Social Media Links */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {/* Facebook */}
              {SOCIAL_MEDIA_LINKS.facebook && (
                <Button
                  component="a"
                  href={SOCIAL_MEDIA_LINKS.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  startIcon={<Facebook />}
                  sx={{
                    color: "white",
                    justifyContent: "flex-start",
                    textTransform: "none",
                    background: "rgba(24,119,242,0.2)",
                    border: "1px solid rgba(24,119,242,0.3)",
                    borderRadius: 2,
                    px: 2,
                    py: 1,
                    "&:hover": {
                      background: "#1877f2",
                      transform: "translateX(5px)",
                      boxShadow: "0 4px 15px rgba(24,119,242,0.4)",
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  Facebook
                </Button>
              )}

              {/* Instagram */}
              {SOCIAL_MEDIA_LINKS.instagram && (
                <Button
                  component="a"
                  href={SOCIAL_MEDIA_LINKS.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  startIcon={<Instagram />}
                  sx={{
                    color: "white",
                    justifyContent: "flex-start",
                    textTransform: "none",
                    background: "rgba(228,64,95,0.2)",
                    border: "1px solid rgba(228,64,95,0.3)",
                    borderRadius: 2,
                    px: 2,
                    py: 1,
                    "&:hover": {
                      background:
                        "linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)",
                      transform: "translateX(5px)",
                      boxShadow: "0 4px 15px rgba(228,64,95,0.4)",
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  Instagram
                </Button>
              )}

              {/* LinkedIn */}
              {SOCIAL_MEDIA_LINKS.linkedin && (
                <Button
                  component="a"
                  href={SOCIAL_MEDIA_LINKS.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  startIcon={<LinkedIn />}
                  sx={{
                    color: "white",
                    justifyContent: "flex-start",
                    textTransform: "none",
                    background: "rgba(0,119,181,0.2)",
                    border: "1px solid rgba(0,119,181,0.3)",
                    borderRadius: 2,
                    px: 2,
                    py: 1,
                    "&:hover": {
                      background: "#0077b5",
                      transform: "translateX(5px)",
                      boxShadow: "0 4px 15px rgba(0,119,181,0.4)",
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  LinkedIn
                </Button>
              )}

              {/* WhatsApp - Coming Soon */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  color: "rgba(255,255,255,0.5)",
                  background: "rgba(37,211,102,0.1)",
                  border: "1px solid rgba(37,211,102,0.2)",
                  borderRadius: 2,
                  px: 2,
                  py: 1,
                  position: "relative",
                }}
              >
                <WhatsApp sx={{ mr: 1, fontSize: 20 }} />
                <Typography variant="body2">WhatsApp</Typography>
                <Chip
                  label="Soon"
                  size="small"
                  sx={{
                    ml: "auto",
                    height: 20,
                    fontSize: "0.7rem",
                    background: "rgba(67,233,123,0.3)",
                    color: "#4ade80",
                  }}
                />
              </Box>

              {/* YouTube - Coming Soon */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  color: "rgba(255,255,255,0.5)",
                  background: "rgba(255,0,0,0.1)",
                  border: "1px solid rgba(255,0,0,0.2)",
                  borderRadius: 2,
                  px: 2,
                  py: 1,
                  position: "relative",
                }}
              >
                <YouTube sx={{ mr: 1, fontSize: 20 }} />
                <Typography variant="body2">YouTube</Typography>
                <Chip
                  label="Soon"
                  size="small"
                  sx={{
                    ml: "auto",
                    height: 20,
                    fontSize: "0.7rem",
                    background: "rgba(255,77,77,0.3)",
                    color: "#ff4d4d",
                  }}
                />
              </Box>
            </Box>
          </Grid>
        </Grid>

        {/* Newsletter Subscription */}
        <Box
          sx={{
            mt: 6,
            pt: 4,
            borderTop: "1px solid rgba(255,255,255,0.1)",
          }}
        >
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
                📬 Stay Updated
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.7 }}>
                Subscribe to our newsletter for the latest updates, tips, and
                exclusive offers.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Box
                  sx={{
                    flex: 1,
                    background: "rgba(255,255,255,0.1)",
                    borderRadius: 2,
                    px: 2,
                    py: 1.5,
                    border: "1px solid rgba(255,255,255,0.2)",
                  }}
                >
                  <Typography variant="body2" sx={{ opacity: 0.7 }}>
                    Enter your email
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  sx={{
                    background: "linear-gradient(135deg, #667eea, #764ba2)",
                    px: 4,
                    borderRadius: 2,
                    fontWeight: "bold",
                    "&:hover": {
                      background: "linear-gradient(135deg, #5a67d8, #6b46c1)",
                    },
                  }}
                >
                  Subscribe
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Bottom Bar */}
        <Box
          sx={{
            mt: 6,
            pt: 4,
            borderTop: "1px solid rgba(255,255,255,0.1)",
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Typography
            variant="body2"
            sx={{ opacity: 0.7, textAlign: { xs: "center", md: "left" } }}
          >
            © {COMPANY_INFO.founded} {COMPANY_INFO.name}. All rights reserved.
            Made with ❤️ for social media excellence.
          </Typography>

          <Box
            sx={{
              display: "flex",
              gap: 3,
              flexWrap: "wrap",
              justifyContent: "center",
            }}
          >
            {FOOTER_LINKS.legal.map((link) => (
              <Button
                key={link.label}
                component="a"
                href={link.url}
                variant="text"
                sx={{
                  color: "rgba(255,255,255,0.7)",
                  textTransform: "none",
                  fontSize: "0.85rem",
                  p: 0,
                  minWidth: "auto",
                  "&:hover": {
                    color: "white",
                    background: "transparent",
                  },
                }}
              >
                {link.label}
              </Button>
            ))}
          </Box>
        </Box>

        {/* Trust Badges */}
        <Box
          sx={{
            mt: 4,
            display: "flex",
            justifyContent: "center",
            gap: 3,
            flexWrap: "wrap",
            opacity: 0.6,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Security sx={{ fontSize: 20 }} />
            <Typography variant="caption">SSL Secured</Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <VerifiedUser sx={{ fontSize: 20 }} />
            <Typography variant="caption">GDPR Compliant</Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CloudSync sx={{ fontSize: 20 }} />
            <Typography variant="caption">99.9% Uptime</Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Support sx={{ fontSize: 20 }} />
            <Typography variant="caption">24/7 Support</Typography>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
